import type { PayableInvoice } from '../../types/payments';
import { formatIneligibleReason } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { dueDateSortKey, formatInvoiceDate, formatPropertyLabel } from '../../lib/payments';

interface InvoiceQueueProps {
  invoices: PayableInvoice[];
  /** True while a payment run is in flight — disables every Pay button. */
  busy?: boolean;
  onPay: (invoice: PayableInvoice) => void;
}

/**
 * The weekly queue (CHO-754): every payable invoice, eligible or not.
 * Ineligible rows stay visible with their reason — hiding them would make
 * "something is stuck" indistinguishable from "nothing left to pay". Ordered
 * by due date; a weekly run exists to pay on time, not late.
 */
export function InvoiceQueue({ invoices, busy = false, onPay }: InvoiceQueueProps) {
  const sorted = [...invoices].sort((a, b) => dueDateSortKey(a).localeCompare(dueDateSortKey(b)));

  if (sorted.length === 0) {
    return (
      <p className="text-gray-500 bg-white border border-gray-200 rounded-lg px-4 py-8 text-center">
        The queue is empty — nothing is waiting to be paid.
      </p>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th className="px-4 py-3 font-medium">Property</th>
            <th className="px-4 py-3 font-medium">Biller</th>
            <th className="px-4 py-3 font-medium text-right">Amount</th>
            <th className="px-4 py-3 font-medium">Invoice date</th>
            <th className="px-4 py-3 font-medium">Due date</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((invoice) => (
            <tr key={invoice.id} className="border-b border-gray-50 last:border-0">
              <td className="px-4 py-3 font-medium text-gray-900">{formatPropertyLabel(invoice)}</td>
              <td className="px-4 py-3 text-gray-600">{invoice.billerName ?? invoice.biller ?? '—'}</td>
              <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">
                {formatCurrency(invoice.amount, invoice.currency)}
              </td>
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatInvoiceDate(invoice.date)}</td>
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                {invoice.dueDate ? (
                  formatInvoiceDate(invoice.dueDate)
                ) : (
                  // CHO-807: a null due date on an ingested invoice means the
                  // PDF genuinely states none — say so instead of a bare dash.
                  <span className="text-gray-400 italic">None stated</span>
                )}
              </td>
              <td className="px-4 py-3">
                {invoice.eligibility.payable ? (
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                    Eligible
                  </span>
                ) : (
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
                    title={invoice.eligibility.reasons.join('\n')}
                  >
                    {invoice.eligibility.reasons.map(formatIneligibleReason).join('; ') || 'Ineligible'}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {invoice.eligibility.payable ? (
                  <button
                    type="button"
                    onClick={() => onPay(invoice)}
                    disabled={busy}
                    className="px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Pay
                  </button>
                ) : (
                  <span className="text-xs text-gray-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
